import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Search, CreditCard, Pencil, Trash2 } from 'lucide-react';

interface Plan {
  id: string;
  name: string;
  description: string | null;
  maxUsers: number;
  maxChannels: number;
  priceMonthly: number;
  features: string[];
  integrations: string[];
  isActive: boolean;
}

function parseArray(value: any): string[] {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

export default function PlansListPage() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deletingPlanId, setDeletingPlanId] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const res = await fetch('/api/super-admin/plans', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
        },
      });
      const data = await res.json();
      setPlans(data.data || []);
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Estás seguro de eliminar este plan?')) return;

    setDeletingPlanId(id);
    try {
      const res = await fetch(`/api/super-admin/plans/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
        },
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || 'Error al eliminar plan');
      }

      fetchPlans();
    } catch (error: any) {
      alert(error.message);
    } finally {
      setDeletingPlanId(null);
    }
  };

  const filteredPlans = plans.filter(
    (p) =>
      p.name?.toLowerCase().includes(search.toLowerCase()) ||
      (p.description && p.description.toLowerCase().includes(search.toLowerCase()))
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#233554] border-t-[#229E64] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-lyrax-text">Planes</h1>
          <p className="text-lyrax-text-secondary">{plans.length} planes configurados</p>
        </div>
        <Link to="/plans/new" className="btn-primary flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Nuevo Plan
        </Link>
      </div>

      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-lyrax-text-secondary" />
          <input
            type="text"
            placeholder="Buscar por nombre o descripción..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input pl-10"
          />
        </div>
      </div>

      <div className="bg-lyrax-surface rounded-xl shadow-sm border border-lyrax-border overflow-hidden">
        <table className="w-full">
          <thead className="bg-lyrax-surface-hover border-b border-lyrax-border">
            <tr>
              <th className="text-left px-6 py-3 text-xs font-medium text-lyrax-text-secondary uppercase tracking-wider">
                Plan
              </th>
              <th className="text-left px-6 py-3 text-xs font-medium text-lyrax-text-secondary uppercase tracking-wider">
                Precio
              </th>
              <th className="text-left px-6 py-3 text-xs font-medium text-lyrax-text-secondary uppercase tracking-wider">
                Límites
              </th>
              <th className="text-left px-6 py-3 text-xs font-medium text-lyrax-text-secondary uppercase tracking-wider">
                Features
              </th>
              <th className="text-left px-6 py-3 text-xs font-medium text-lyrax-text-secondary uppercase tracking-wider">
                Estado
              </th>
              <th className="text-right px-6 py-3 text-xs font-medium text-lyrax-text-secondary uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-lyrax-border">
            {filteredPlans.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-lyrax-text-secondary">
                  {search ? 'No se encontraron planes' : 'No hay planes creados'}
                </td>
              </tr>
            ) : (
              filteredPlans.map((plan) => (
                <tr key={plan.id} className="hover:bg-lyrax-surface-hover">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-lyrax-primary/10 rounded-lg flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-lyrax-primary" />
                      </div>
                      <div>
                        <Link to={`/plans/${plan.id}`} className="font-medium text-lyrax-text hover:text-lyrax-primary">
                          {plan.name}
                        </Link>
                        {plan.description && (
                          <p className="text-sm text-lyrax-text-secondary truncate max-w-xs">
                            {plan.description}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="font-medium text-lyrax-text">
                      ${(plan.priceMonthly ?? 0).toLocaleString('es-CL')}
                    </span>
                    <span className="text-lyrax-text-secondary text-sm">/mes</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-lyrax-text">
                      <span className="text-lyrax-text-secondary">Users: </span>
                      {plan.maxUsers === 0 ? '∞' : plan.maxUsers}
                    </div>
                    <div className="text-sm text-lyrax-text">
                      <span className="text-lyrax-text-secondary">Canales: </span>
                      {plan.maxChannels === 0 ? '∞' : plan.maxChannels}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-wrap gap-1">
                      {parseArray(plan.features).slice(0, 3).map((feature) => (
                        <span
                          key={feature}
                          className="px-2 py-0.5 bg-lyrax-primary/10 text-lyrax-primary text-xs rounded"
                        >
                          {feature}
                        </span>
                      ))}
                      {parseArray(plan.features).length > 3 && (
                        <span className="px-2 py-0.5 bg-lyrax-surface-hover text-lyrax-text-secondary text-xs rounded">
                          +{parseArray(plan.features).length - 3}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        plan.isActive
                          ? 'bg-lyrax-success/10 text-lyrax-success'
                          : 'bg-lyrax-surface-hover text-lyrax-text-secondary'
                      }`}
                    >
                      {plan.isActive ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => navigate(`/plans/${plan.id}`)}
                        className="p-2 text-lyrax-text-secondary hover:text-lyrax-primary transition-colors"
                        title="Editar"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(plan.id)}
                        disabled={deletingPlanId === plan.id}
                        className="p-2 text-lyrax-text-secondary hover:text-red-400 transition-colors"
                        title="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
